import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { askQuestion } from './api';
import { Icon } from './Controls';
import ChatActivity from './ChatActivity';
import { EMPTY_CONTEXT, contextSummary } from './QuestionContext';

const EXAMPLE_QUESTIONS = [
  'I am interested in Arts but unsure which programme to choose.',
  'How do I apply to HKU Arts?',
  'What are the BA admission requirements?',
  'When is the application deadline?'
];

function remarkSourceCitations(numbers) {
  return () => (tree) => {
    function visit(node) {
      if (!node.children) return;
      node.children = node.children.flatMap((child) => {
        if (child.type !== 'text') {
          if (child.type !== 'link' && child.type !== 'linkReference') visit(child);
          return [child];
        }
        const pieces = child.value.split(/(\[\d+(?:\s*[,，]\s*\d+)*\])/g);
        return pieces.filter(Boolean).flatMap((piece) => {
          const match = piece.match(/^\[(\d+(?:\s*[,，]\s*\d+)*)\]$/);
          if (!match) return [{ type: 'text', value: piece }];
          return match[1].split(/[,，]/).flatMap((value, index) => {
            const number = Number(value);
            const citation = numbers.has(number)
              ? { type: 'link', url: `#source-${number}`, children: [{ type: 'text', value: `[${number}]` }] }
              : { type: 'text', value: `[${number}]` };
            return index ? [{ type: 'text', value: ' ' }, citation] : [citation];
          });
        });
      });
    }
    visit(tree);
  };
}

function AssistantResponse({ message, onQuickReply, allowQuickReplies, onRetry, onSources }) {
  const [copyState, setCopyState] = useState('');
  const numbers = new Set((message.sources || []).map((source) => source.number));
  const pending = message.status === 'streaming';
  const gaps = (message.coverage || []).filter((item) => item.status === 'not_confirmed');
  const enquiry = `Hello, I would like to ask about HKU Faculty of Arts admissions.\n\n${contextSummary(message.context) || 'Programme / intake not yet specified'}\n\nMy question: ${message.question}\n\n${gaps.length ? `I could not confirm: ${gaps.map((item) => `${item.topic}: ${item.detail}`).join('; ')}` : 'The demo documents did not contain enough information.'}\n\nCould you please advise me on the next step?`;
  const showSource = (number) => onSources(message.id, number);
  async function copyEnquiry() {
    try {
      await navigator.clipboard.writeText(enquiry);
      setCopyState('copied');
    } catch {
      setCopyState('manual');
    }
  }
  return (
    <div className="prose-sm max-w-none">
      <ChatActivity steps={message.steps} status={message.status} />
      <div className="arts-answer break-words" aria-label={pending ? 'Answer being written' : 'Assistant answer'}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkSourceCitations(numbers)]}
          components={{
            a: ({ href, children }) => {
              const match = href?.match(/^#source-(\d+)$/);
              if (match && numbers.has(Number(match[1]))) {
                return <button type="button" className="text-link underline" onClick={() => showSource(Number(match[1]))} aria-label={`Show source ${match[1]}`}>{children}</button>;
              }
              return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
            }
          }}
        >{message.content}</ReactMarkdown>
      </div>
      {(message.quickReplies || []).length > 0 && allowQuickReplies && (
        <div className="mt-3 flex flex-wrap gap-2" aria-label="Suggested replies">
          {message.quickReplies.map((choice) => (
            <button key={choice} type="button" onClick={() => onQuickReply(choice)} className="rounded-full border border-edge px-3 py-2 text-xs text-link hover:border-primary">{choice}</button>
          ))}
        </div>
      )}
      {message.sources?.length > 0 && <button type="button" className="mt-4 text-xs text-link" onClick={() => onSources(message.id)}>References · {message.sources.length} ↗</button>}
      {message.status === 'complete' && (gaps.length > 0 || message.kind === 'insufficient') && (
        <div className="mt-3 text-xs">
          <button type="button" className="text-link" onClick={copyEnquiry}>Copy enquiry draft</button>
          {copyState === 'copied' && <p className="mt-1 text-ink-muted" role="status">Copied. Review it before sending to admissions.</p>}
          {copyState === 'manual' && <textarea aria-label="Enquiry draft to copy" readOnly value={enquiry} rows={7} className="mt-2 w-full rounded-lg border border-edge bg-surface p-2" />}
        </div>
      )}
      {['error', 'stopped'].includes(message.status) && (
        <div className="mt-3 rounded-lg border border-edge p-3 text-xs" role="status">
          <p>{message.status === 'stopped' ? 'Stopped. This reply is incomplete.' : message.error}</p>
          {message.content && <p className="mt-1 text-ink-muted">The partial reply above has not completed its source check.</p>}
          {onRetry && <button type="button" className="mt-2 text-link" onClick={onRetry}>Retry question</button>}
        </div>
      )}
    </div>
  );
}

export default function StudentChat({ conversation, onChange, onSources, active, onOpenKnowledge }) {
  const [messages, setMessages] = useState(conversation.messages || []);
  const [draft, setDraft] = useState('');
  const [context, setContext] = useState({ ...EMPTY_CONTEXT, ...conversation.context });
  const [pending, setPending] = useState(false);
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => { onChange(conversation.id, { messages, context }); }, [conversation.id, messages, context, onChange]);
  const activeRequest = useRef(null);
  const followOutput = useRef(true);

  useEffect(() => {
    const onScroll = () => {
      const element = scrollRef.current;
      const nearBottom = !element || element.scrollHeight - element.scrollTop - element.clientHeight < 120;
      followOutput.current = nearBottom;
      setAwayFromBottom(!nearBottom);
    };
    const element = scrollRef.current;
    element?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      element?.removeEventListener('scroll', onScroll);
      activeRequest.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (active && followOutput.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, active]);

  async function sendQuestion(text, retryFrom = null) {
    const question = text.trim();
    if (!question || activeRequest.current) return;
    const base = retryFrom == null ? messages : messages.slice(0, retryFrom);
    const history = base.filter((item) => item.role === 'user' || item.status === 'complete').map(({ role, content }) => ({ role, content }));
    const controller = new AbortController();
    const id = `${Date.now()}-${Math.random()}`;
    activeRequest.current = controller;
    followOutput.current = true;
    const update = (change) => {
      if (controller.signal.aborted) return;
      setMessages((current) => current.map((item) => item.id === id ? change(item) : item));
    };
    setMessages([...base, { id: `${id}-user`, role: 'user', content: question }, {
      id, role: 'assistant', content: '', status: 'streaming', steps: [], sources: [], coverage: [], context: { ...context }, question
    }]);
    setDraft('');
    setPending(true);
    try {
      const result = await askQuestion(question, history, {
        context, signal: controller.signal,
        onProgress: (step) => update((item) => ({ ...item, steps: item.steps.some((previous) => previous.id === step.id) ? item.steps.map((previous) => previous.id === step.id ? step : previous) : [...item.steps, step] })),
        onDelta: (text) => update((item) => ({ ...item, content: item.content + text })),
        onMetadata: (metadata) => {
          update((item) => ({ ...item, ...metadata }));
          if (!controller.signal.aborted && metadata.context) setContext({ ...EMPTY_CONTEXT, ...metadata.context });
        }
      });
      update((item) => ({ ...item, ...result, content: result.answer, status: 'complete' }));
      if (!controller.signal.aborted && result.context) setContext({ ...EMPTY_CONTEXT, ...result.context });
    } catch (error) {
      setMessages((current) => current.map((item) => item.id === id ? {
        ...item, status: controller.signal.aborted ? 'stopped' : 'error', error: error.message || 'The reply could not finish. Please retry.'
      } : item));
    } finally {
      if (activeRequest.current === controller) {
        activeRequest.current = null;
        setPending(false);
      }
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    sendQuestion(draft);
  }

  function stopReply() {
    activeRequest.current?.abort();
    activeRequest.current = null;
    setPending(false);
    setMessages((current) => current.map((item) => item.status === 'streaming' ? { ...item, status: 'stopped' } : item));
  }

  return (
    <div className="arts-chat">
      <div ref={scrollRef} className="arts-transcript">
        <div className="arts-transcript-inner">
        {!messages.length && (
          <div className="arts-welcome">
            <p className="text-xs tracking-widest uppercase text-ink-muted">HKU Faculty of Arts</p>
            <h2 className="text-3xl font-medium mt-3 mb-8">What would you like to explore?</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {EXAMPLE_QUESTIONS.slice(0, 3).map((question) => (
                <button key={question} type="button" onClick={() => sendQuestion(question)} className="arts-pill border border-edge bg-surface px-4 py-3 text-sm text-ink-muted hover:border-primary">{question}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((message, index) => (
          <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`min-w-0 rounded-2xl px-4 py-3 text-sm ${message.role === 'user' ? 'max-w-[90%] bg-primary text-primary-foreground' : 'w-full text-ink'}`}>
              {message.role === 'assistant' ? (
                <AssistantResponse onSources={onSources} message={message} onQuickReply={sendQuestion} allowQuickReplies={!pending && index === messages.length - 1} onRetry={!pending ? () => sendQuestion(message.question, index - 1) : null} />
              ) : message.content}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
        </div>
      </div>
      {pending && awayFromBottom && <button type="button" className="mt-2 self-center rounded-full border border-edge bg-surface px-4 py-2 text-sm text-link" onClick={() => { followOutput.current = true; bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }}>Jump to live reply ↓</button>}
      <form onSubmit={onSubmit} className="arts-composer"><div className="arts-input-pill">
        <button type="button" className="arts-icon-button" aria-label="Open knowledge base" title="Knowledge base" onClick={onOpenKnowledge}><Icon name="gear" /></button>
        <input value={draft} maxLength={2000} aria-label="Your question about HKU Arts" onChange={(event) => setDraft(event.target.value)} placeholder="Ask about an HKU Arts programme or requirement" className="arts-message-input" />
        {pending ? (
          <button type="button" onClick={stopReply} className="arts-icon-button arts-send" aria-label="Stop reply" title="Stop reply"><Icon name="stop" /></button>
        ) : (
          <button type="submit" disabled={!draft.trim()} className="arts-icon-button arts-send" aria-label="Send message" title="Send message"><Icon name="arrow" /></button>
        )}
      </div></form>
    </div>
  );
}
